import { eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@tendly/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT disappear silently ─────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can match
 * **ZERO rows and return "success"**. Measured in `space-academy-app` (same
 * canon, the local stack's `activity` table, course `06a131d0`): the course is
 * created, 1.03 s later the unit is created, and **in between there is NO
 * `course/updated` operation** — the title the author just wrote never generated
 * a CRUD operation.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the local
 * view from the last checkpoint**. If the checkpoint still does not contain the
 * newly created row, the row is **ABSENT for that instant** — and an UPDATE
 * against it writes nothing, generates no operation, and **does not fail**.
 *
 * Why the absence of an error proves absolutely nothing here: the server
 * write-path intentionally returns **2xx every time** (a 4xx would block the
 * client queue forever). A user write that vanishes without a trace is the
 * worst failure mode — worse than a visible error.
 *
 * This module's contract: write, **READ BACK**, and compare. If the value did
 * not land, retry with backoff; if it still has not landed when retries are
 * exhausted, **THROW** — the caller reports it instead of leaving the user
 * looking at a field that represents nothing saved.
 *
 * ── What this module does NOT cover (deliberately) ──────────────────────────
 * Hard `DELETE`s (`hardDeleteAssetMutation`, `hardDeleteServiceRecordMutation`,
 * `hardDeleteWarrantyMutation`, `deleteUsageReadingMutation`,
 * `deleteAttachmentMutation`) are not verified: their natural check would be “the
 * row is gone”, and “absent” is exactly the ambiguous state of this window — we
 * cannot distinguish “it was deleted” from “it had not arrived yet”. Verifying
 * them would require another signal (the CRUD queue itself), not a re-read. A
 * signal that would warrant reopening this: a hard delete genuinely getting lost
 * (the row reappearing after deletion).
 */

/**
 * Retry backoff. Sums to ≈ 6.05 s: amply covers a checkpoint round trip
 * without leaving a quick interaction —renaming an asset, marking a reminder
 * done— hanging forever if the row really does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
}

/** Error from a local-first write that did not land in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Reads the row by id, or `undefined` if it is not in the local view. */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read is by
  // primary key, so the row is a flat record of columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const rows = (await db
    .select()
    .from(table)
    .where(eq(idColumn, id))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when compared against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, reads back, compares the patch
 * fields and retries while they have not landed. Throws `LocalWriteLostError`
 * if they are still missing when the backoff is exhausted.
 *
 * `updated_at` is set by this function on every attempt (and is not compared:
 * it changes on every pass); `patch` must contain only domain fields.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(eq(idColumn, id));

    const row = await readRow(db, table, idColumn, id);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Waits until the row EXISTS in the local view before touching it.
 *
 * For mutations that are not a simple patch: a transaction cannot retry
 * inside itself (it would sleep with the write lock held), and the damage
 * from operating on an absent row is usually worse than losing a field — for
 * example, a sibling UPDATE that matches nothing while the neighboring INSERT
 * does occur. No write path in this repo uses transactions yet (see
 * `packages/shared/src/tendly/mutations.ts`), so it has no consumers today —
 * it is kept ready for when one appears, as in the canon (`listto-app`).
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}
